/**
 * The minimum Teslyn Field version for sync (MB decision 497; docs/specs/sync/min-field-version.md).
 *
 * When a Teslyn Cloud change needs a newer Teslyn Field, Braynewave raises the minimum (`platform_setting`
 * `field_min_sync_version`). A laptop below it keeps working offline, says "Update Teslyn Field to keep syncing" and
 * keeps its changes queued until it is updated; `public.sync_apply` refuses its uploads (code `field_too_old`).
 *
 * The rule lives here. The database holds a copy of it, `public.field_may_sync` (migration
 * 20261024000000_field_min_sync_version.sql), because the cloud must refuse an old laptop that never asks; the two
 * are tested on the same cases (packages/domain/test/field-version.test.ts, supabase/tests/rls-field-min-version.test.ts).
 */

/**
 * Teslyn Field 2026.10.5 and older send no version with their uploads: such a laptop is taken to run this version,
 * so it syncs until the minimum is raised past it.
 */
export const FIELD_VERSION_UNREPORTED = '2026.10.5';

/** The version of a development build (apps/field/package.json before the release workflow stamps it). */
export const FIELD_DEV_VERSION = '0.0.0';

const VERSION = /^(\d{1,4})\.(\d{1,3})\.(\d{1,5})(?:-[0-9A-Za-z.-]{1,30})?$/;
const MINIMUM = /^\d{4}\.\d{1,2}\.\d{1,4}$/;

/**
 * Year, month and patch of a Teslyn Field version (`2026.10.5`, `2026.11.0-beta.2`), or null for text that is not
 * one. The pre-release is dropped: a beta of a version meets that version as a minimum.
 */
export function fieldVersionParts(text: string): readonly [number, number, number] | null {
  const m = VERSION.exec(text.trim());
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Negative when `a` is older than `b`, zero when they are the same release. Throws on text that is not a version. */
export function compareFieldVersions(a: string, b: string): number {
  const x = fieldVersionParts(a);
  const y = fieldVersionParts(b);
  if (!x || !y) throw new Error(`not a Teslyn Field version: ${!x ? a : b}`);
  for (let i = 0; i < 3; i++) {
    const d = x[i]! - y[i]!;
    if (d !== 0) return d;
  }
  return 0;
}

/** Whether text may be stored as the minimum: a plain release version, `YYYY.M.patch`, nothing around it. */
export const isMinimumFieldVersion = (text: string): boolean => MINIMUM.test(text);

/**
 * Whether a laptop running `running` may sync while the minimum is `minimum`.
 * - No minimum: every laptop syncs.
 * - No version reported (Teslyn Field 2026.10.5 and older): taken as {@link FIELD_VERSION_UNREPORTED}.
 * - A development build ({@link FIELD_DEV_VERSION}) syncs: it is built from the current source.
 * - A version that cannot be read is refused once a minimum is set.
 *
 * The version is what the laptop says it runs: the rule keeps old builds from writing in a shape the cloud no longer
 * takes; it is not an access control (RLS is).
 */
export function fieldMaySync(
  running: string | null | undefined,
  minimum: string | null | undefined,
): boolean {
  if (!minimum) return true;
  const min = fieldVersionParts(minimum);
  if (!min) return true; // the database only stores readable minimums; an unreadable one never locks laptops out
  const version = running?.trim() || FIELD_VERSION_UNREPORTED;
  if (version === FIELD_DEV_VERSION) return true;
  if (!fieldVersionParts(version)) return false;
  return compareFieldVersions(version, minimum) >= 0;
}

// ---------------------------------------------------------------- the recommended version (decision 523)

/**
 * The recommended Teslyn Field version (`platform_setting` `field_recommended_version`) and the day from which it is
 * needed (`field_recommended_from`, optional): Braynewave sets it before raising the minimum, so crews update in
 * their own time. A laptop below it keeps syncing and shows "Update Teslyn Field soon" (dismissed for a day at a
 * time); the minimum, when raised later, is what stops sync. Runbook: docs/specs/sync/min-field-version.md §6.
 */
export interface RecommendedFieldVersion {
  /** A plain release version (`isMinimumFieldVersion`). */
  readonly version: string;
  /** `YYYY-MM-DD`: the day the minimum is to be raised to this version; null when not decided. */
  readonly from: string | null;
}

/** Whether text is a calendar day `YYYY-MM-DD` (the recommended version's cut-off date). */
export function isCutOffDate(text: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === mo - 1 && t.getUTCDate() === d;
}

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const dayText = (iso: string): string =>
  `${Number(iso.slice(8, 10))} ${MONTH[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

export interface UpdateSoonNotice {
  readonly heading: string;
  readonly text: string;
}

/**
 * The "Update Teslyn Field soon" notice of a laptop running `running`, or null when it has none: no recommended
 * version, the laptop meets it (the same comparison as the minimum: a beta of the version meets it, a development
 * build always does, no version reported is 2026.10.5), or the laptop is already below the minimum (that banner says
 * more).
 */
export function updateSoonNotice(
  running: string | null | undefined,
  recommended: RecommendedFieldVersion | null | undefined,
  minimum: string | null | undefined,
): UpdateSoonNotice | null {
  if (!recommended || !isMinimumFieldVersion(recommended.version)) return null;
  if (!fieldMaySync(running, minimum)) return null;
  if (fieldMaySync(running, recommended.version)) return null;
  const runs = running?.trim() || FIELD_VERSION_UNREPORTED;
  const from = recommended.from && isCutOffDate(recommended.from) ? recommended.from : null;
  return {
    heading: 'Update Teslyn Field soon',
    text: from
      ? `Update Teslyn Field soon: version ${recommended.version} is needed from ${dayText(from)}. This laptop runs ${runs} and keeps syncing until then.`
      : `Update Teslyn Field soon: version ${recommended.version} is recommended. This laptop runs ${runs} and keeps syncing.`,
  };
}
